<template>
  <div>
    <StoreSwitcher v-if="isAdmin" />
    <b-tabs content-class="mt-3">
      <b-tab title="Caixas" active>
        <CheckoutsFilter v-model="filter" @filter="loadCheckoutTab" />
        <div class="d-flex justify-content-end mb-2">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="showCheckoutPhotoFilters = !showCheckoutPhotoFilters"
          >
            <span
              :class="
                showCheckoutPhotoFilters
                  ? 'fas fa-chevron-up'
                  : 'fas fa-chevron-down'
              "
              class="mr-2"
            />
            {{
              showCheckoutPhotoFilters
                ? 'Ocultar filtros de fotos'
                : 'Mostrar filtros de fotos'
            }}
          </b-button>
        </div>
        <b-collapse v-model="showCheckoutPhotoFilters">
          <b-card class="mb-3">
            <b-row>
              <b-col sm="12" md="3">
                <b-form-group
                  label="Fotos de caderno"
                  label-for="notebook-photo-status"
                  class="mb-md-0"
                >
                  <b-form-select
                    id="notebook-photo-status"
                    v-model="checkoutPhotoFilter.closingNotebook"
                    :options="photoStatusOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col sm="12" md="3">
                <b-form-group
                  label="Fotos da maquininha"
                  label-for="machine-photo-status"
                  class="mb-md-0"
                >
                  <b-form-select
                    id="machine-photo-status"
                    v-model="checkoutPhotoFilter.machineReceipt"
                    :options="photoStatusOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col sm="12" md="3">
                <b-form-group
                  label="Comprovantes de deposito"
                  label-for="deposit-receipt-status"
                  class="mb-md-0"
                >
                  <b-form-select
                    id="deposit-receipt-status"
                    v-model="checkoutPhotoFilter.depositReceipt"
                    :options="photoStatusOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col sm="12" md="3">
                <b-form-group
                  label="Notas de saidas"
                  label-for="expense-receipt-status"
                  class="mb-md-0"
                >
                  <b-form-select
                    id="expense-receipt-status"
                    v-model="checkoutPhotoFilter.expenseReceipt"
                    :options="photoStatusOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col sm="12" md="3" class="checkout-photo-filter-actions">
                <b-button
                  variant="outline-secondary"
                  @click="clearPhotoFilters"
                >
                  <span class="fas fa-times mr-2" />Limpar filtros de fotos
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <CheckoutsList
          hide-warnings
          is-report
          :is-admin="isAdmin"
          :checkouts="filteredCheckouts"
          @refresh="loadCheckoutTab"
        />
      </b-tab>
      <b-tab title="Fotos">
        <b-row>
          <b-col sm="12" md="4">
            <b-form-group label="Data Inicial" label-for="photo-from-date">
              <b-form-datepicker
                id="photo-from-date"
                v-model="photoFilter.fromDate"
                placeholder="Selecione uma data"
                locale="pt-BR"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="4">
            <b-form-group label="Data Final" label-for="photo-to-date">
              <b-form-datepicker
                id="photo-to-date"
                v-model="photoFilter.toDate"
                placeholder="Selecione uma data"
                locale="pt-BR"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="4">
            <b-form-group label="Tipo" label-for="photo-type">
              <div class="d-flex gap-1">
                <b-form-select
                  id="photo-type"
                  v-model="photoFilter.type"
                  :options="photoTypeOptions"
                />
                <b-button variant="primary" @click="loadCheckoutPhotos">
                  <span class="fas fa-filter mr-2" />Filtrar
                </b-button>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-table
          responsive
          :fields="photoFields"
          :items="reportPhotos"
          :busy="
            checkoutPhotos.loading ||
            depositReceiptPhotos.loading ||
            expenseReceiptPhotos.loading
          "
          striped
          hover
          show-empty
        >
          <div slot="empty" class="text-center">Nenhuma foto encontrada</div>

          <template #cell(checkout)="{ item }">
            <b-link
              :to="{
                name: 'ReportCheckoutDetails',
                params: { id: item.checkoutId }
              }"
            >
              Caixa #{{ item.checkoutId }}
            </b-link>
            <small
              v-if="item.checkout && item.checkout.store"
              class="d-block text-muted"
            >
              {{ item.checkout.store.name }}
            </small>
          </template>

          <template #cell(type)="{ value }">
            {{ PHOTO_TYPE_LABELS[value] || value }}
          </template>

          <template #cell(description)="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell(createdBy)="{ item }">
            {{ item.createdByUser ? item.createdByUser.name : '-' }}
          </template>

          <template #cell(createdAt)="{ value }">
            {{ value | formatDateTime }}
          </template>

          <template #cell(actions)="{ item }">
            <b-button
              v-b-tooltip.hover
              size="sm"
              variant="outline-primary"
              title="Ver foto"
              @click="openPhoto(item)"
            >
              <span class="fas fa-image" />
            </b-button>
          </template>
        </b-table>
      </b-tab>
    </b-tabs>

    <b-modal
      v-model="photoPreview.visible"
      title="Foto do caixa"
      size="lg"
      hide-footer
    >
      <img
        v-if="photoPreview.url"
        :src="photoPreview.url"
        alt="Foto do caixa"
        class="checkout-photo-preview"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import CheckoutsFilter from '@/components/Checkouts/CheckoutsFilter'
import CheckoutsList from '@/components/Checkouts/CheckoutsList'
import { formatDate, formatDateTime } from '@/helpers/date'
import StoreSwitcher from '@/components/Stores/StoreSwitcher'
import { CHECKOUT_PHOTO_TYPES, CHECKOUT_PHOTO_TYPE_LABELS } from '@/constants'

const DEPOSIT_RECEIPT_TYPE = 'depositReceipt'
const EXPENSE_RECEIPT_TYPE = 'expenseReceipt'
const PHOTO_TYPE_LABELS = {
  ...CHECKOUT_PHOTO_TYPE_LABELS,
  [DEPOSIT_RECEIPT_TYPE]: 'Recibo de deposito',
  [EXPENSE_RECEIPT_TYPE]: 'Nota/comprovante de saida'
}

export default {
  name: 'Checkouts',
  components: {
    CheckoutsFilter,
    CheckoutsList,
    StoreSwitcher
  },
  filters: {
    formatDateTime
  },
  mixins: [
    withAsyncAction({
      key: 'checkouts',
      initialState: [],
      fetcher: {
        methodName: 'fetchCheckouts',
        handler: services.checkouts.fetchCheckouts
      }
    }),
    withAsyncAction({
      key: 'checkoutPhotos',
      initialState: [],
      fetcher: {
        methodName: 'fetchCheckoutPhotos',
        handler: services.checkouts.fetchCheckoutPhotos
      }
    }),
    withAsyncAction({
      key: 'checkoutPhotoIndex',
      initialState: [],
      fetcher: {
        methodName: 'fetchCheckoutPhotoIndex',
        handler: services.checkouts.fetchCheckoutPhotos
      }
    }),
    withAsyncAction({
      key: 'depositReceiptIndex',
      initialState: [],
      fetcher: {
        methodName: 'fetchDepositReceiptIndex',
        handler: services.operations.fetchBankDeposits
      }
    }),
    withAsyncAction({
      key: 'depositReceiptPhotos',
      initialState: [],
      fetcher: {
        methodName: 'fetchDepositReceiptPhotos',
        handler: services.operations.fetchBankDeposits
      }
    }),
    withAsyncAction({
      key: 'expenseReceiptIndex',
      initialState: [],
      fetcher: {
        methodName: 'fetchExpenseReceiptIndex',
        handler: services.operations.fetchCashExpenses
      }
    }),
    withAsyncAction({
      key: 'expenseReceiptPhotos',
      initialState: [],
      fetcher: {
        methodName: 'fetchExpenseReceiptPhotos',
        handler: services.operations.fetchCashExpenses
      }
    })
  ],
  data() {
    return {
      filter: {
        fromDate: formatDate(new Date()),
        toDate: formatDate(new Date())
      },
      photoFilter: {
        fromDate: formatDate(new Date()),
        toDate: formatDate(new Date()),
        type: null
      },
      checkoutPhotoFilter: {
        closingNotebook: null,
        machineReceipt: null,
        depositReceipt: null,
        expenseReceipt: null
      },
      showCheckoutPhotoFilters: false,
      photoPreview: {
        visible: false,
        url: null
      }
    }
  },
  computed: {
    ...mapGetters('store', ['currentStoreId', 'isAdmin']),
    photoTypeOptions() {
      return [
        { text: 'Todos', value: null },
        ...Object.values(CHECKOUT_PHOTO_TYPES).map((value) => ({
          value,
          text: CHECKOUT_PHOTO_TYPE_LABELS[value]
        })),
        {
          text: PHOTO_TYPE_LABELS[DEPOSIT_RECEIPT_TYPE],
          value: DEPOSIT_RECEIPT_TYPE
        },
        {
          text: PHOTO_TYPE_LABELS[EXPENSE_RECEIPT_TYPE],
          value: EXPENSE_RECEIPT_TYPE
        }
      ]
    },
    photoStatusOptions() {
      return [
        { text: 'Todos', value: null },
        { text: 'Com fotos', value: 'with' },
        { text: 'Sem fotos', value: 'without' }
      ]
    },
    checkoutPhotoCounts() {
      return this.checkoutPhotoIndex.data.reduce((result, photo) => {
        const checkoutId = Number(photo.checkoutId)
        if (!result[checkoutId]) {
          result[checkoutId] = {
            [CHECKOUT_PHOTO_TYPES.CLOSING_NOTEBOOK]: 0,
            [CHECKOUT_PHOTO_TYPES.MACHINE_RECEIPT]: 0
          }
        }

        result[checkoutId][photo.type] += 1
        return result
      }, {})
    },
    depositReceiptCounts() {
      return this.depositReceiptIndex.data.reduce((result, operation) => {
        const checkoutId = Number(operation.checkoutId)
        if (!result[checkoutId]) {
          result[checkoutId] = {
            withReceipt: 0,
            withoutReceipt: 0
          }
        }

        if (operation.receiptPhotoUrl) {
          result[checkoutId].withReceipt += 1
        } else {
          result[checkoutId].withoutReceipt += 1
        }

        return result
      }, {})
    },
    expenseReceiptCounts() {
      return this.expenseReceiptIndex.data.reduce((result, operation) => {
        const checkoutId = Number(operation.checkoutId)
        if (!result[checkoutId]) {
          result[checkoutId] = {
            withReceipt: 0,
            withoutReceipt: 0
          }
        }

        if (operation.receiptPhotoUrl) {
          result[checkoutId].withReceipt += 1
        } else {
          result[checkoutId].withoutReceipt += 1
        }

        return result
      }, {})
    },
    filteredCheckouts() {
      const data = this.checkouts.data.filter((checkout) =>
        this.matchesPhotoFilters(checkout)
      )

      return {
        ...this.checkouts,
        loading:
          this.checkouts.loading ||
          this.checkoutPhotoIndex.loading ||
          this.depositReceiptIndex.loading ||
          this.expenseReceiptIndex.loading,
        error:
          this.checkouts.error ||
          this.checkoutPhotoIndex.error ||
          this.depositReceiptIndex.error ||
          this.expenseReceiptIndex.error,
        data
      }
    },
    photoFields() {
      return [
        { key: 'checkout', label: 'Caixa' },
        { key: 'type', label: 'Tipo' },
        { key: 'description', label: 'Descricao' },
        { key: 'createdBy', label: 'Anexado por' },
        { key: 'createdAt', label: 'Anexado em' },
        { key: 'actions', label: 'Acoes' }
      ]
    },
    reportPhotos() {
      const operationReceiptTypes = [DEPOSIT_RECEIPT_TYPE, EXPENSE_RECEIPT_TYPE]
      const shouldShowCheckoutPhotos =
        !this.photoFilter.type ||
        !operationReceiptTypes.includes(this.photoFilter.type)
      const shouldShowDepositReceipts =
        !this.photoFilter.type || this.photoFilter.type === DEPOSIT_RECEIPT_TYPE
      const shouldShowExpenseReceipts =
        !this.photoFilter.type || this.photoFilter.type === EXPENSE_RECEIPT_TYPE

      const checkoutPhotos = shouldShowCheckoutPhotos
        ? this.checkoutPhotos.data
        : []
      const depositReceipts = shouldShowDepositReceipts
        ? this.depositReceiptPhotos.data
            .filter((operation) => operation.receiptPhotoUrl)
            .map((operation) => ({
              id: `deposit-${operation.id}`,
              checkoutId: operation.checkoutId,
              checkout: operation.checkout,
              type: DEPOSIT_RECEIPT_TYPE,
              photoUrl: operation.receiptPhotoUrl,
              description: operation.name,
              createdByUser:
                operation.receiptPhotoUploadedByUser || operation.createdByUser,
              createdAt:
                operation.receiptPhotoUploadedAt ||
                operation.updatedAt ||
                operation.createdAt
            }))
        : []

      const expenseReceipts = shouldShowExpenseReceipts
        ? this.expenseReceiptPhotos.data
            .filter((operation) => operation.receiptPhotoUrl)
            .map((operation) => ({
              id: `expense-${operation.id}`,
              checkoutId: operation.checkoutId,
              checkout: operation.checkout,
              type: EXPENSE_RECEIPT_TYPE,
              photoUrl: operation.receiptPhotoUrl,
              description: operation.name,
              createdByUser:
                operation.receiptPhotoUploadedByUser || operation.createdByUser,
              createdAt:
                operation.receiptPhotoUploadedAt ||
                operation.updatedAt ||
                operation.createdAt
            }))
        : []

      return [...checkoutPhotos, ...depositReceipts, ...expenseReceipts]
    }
  },
  watch: {
    currentStoreId() {
      this.loadCheckoutTab()
      this.loadCheckoutPhotos()
    }
  },
  created() {
    this.PHOTO_TYPE_LABELS = PHOTO_TYPE_LABELS
    this.loadCheckoutTab()
    this.loadCheckoutPhotos()
  },
  methods: {
    loadCheckoutTab() {
      this.loadCheckouts()
      this.loadCheckoutPhotoIndex()
      this.loadDepositReceiptIndex()
      this.loadExpenseReceiptIndex()
    },
    loadCheckouts() {
      const storeId = this.currentStoreId
      const { fromDate, toDate } = this.filter
      this.fetchCheckouts({ storeId, fromDate, toDate })
    },
    loadCheckoutPhotoIndex() {
      const storeId = this.currentStoreId
      const { fromDate, toDate } = this.filter
      this.fetchCheckoutPhotoIndex({ storeId, fromDate, toDate })
    },
    loadDepositReceiptIndex() {
      const storeId = this.currentStoreId
      const { fromDate, toDate } = this.filter
      this.fetchDepositReceiptIndex({ storeId, fromDate, toDate })
    },
    loadExpenseReceiptIndex() {
      const storeId = this.currentStoreId
      const { fromDate, toDate } = this.filter
      this.fetchExpenseReceiptIndex({ storeId, fromDate, toDate })
    },
    loadCheckoutPhotos() {
      const storeId = this.currentStoreId
      const { type, ...dateFilter } = this.photoFilter

      if (type === DEPOSIT_RECEIPT_TYPE) {
        this.checkoutPhotos.data = []
        this.expenseReceiptPhotos.data = []
        this.fetchDepositReceiptPhotos({ storeId, ...dateFilter })
        return
      }

      if (type === EXPENSE_RECEIPT_TYPE) {
        this.checkoutPhotos.data = []
        this.depositReceiptPhotos.data = []
        this.fetchExpenseReceiptPhotos({ storeId, ...dateFilter })
        return
      }

      this.fetchCheckoutPhotos({ storeId, ...this.photoFilter })

      if (!type) {
        this.fetchDepositReceiptPhotos({ storeId, ...dateFilter })
        this.fetchExpenseReceiptPhotos({ storeId, ...dateFilter })
      } else {
        this.depositReceiptPhotos.data = []
        this.expenseReceiptPhotos.data = []
      }
    },
    matchesPhotoFilters(checkout) {
      const counts = this.checkoutPhotoCounts[Number(checkout.id)] || {}
      const depositCounts = this.depositReceiptCounts[Number(checkout.id)] || {}
      const expenseCounts = this.expenseReceiptCounts[Number(checkout.id)] || {}
      const notebookCount = counts[CHECKOUT_PHOTO_TYPES.CLOSING_NOTEBOOK] || 0
      const machineCount = counts[CHECKOUT_PHOTO_TYPES.MACHINE_RECEIPT] || 0
      const depositWithReceiptCount = depositCounts.withReceipt || 0
      const depositWithoutReceiptCount = depositCounts.withoutReceipt || 0
      const expenseWithReceiptCount = expenseCounts.withReceipt || 0
      const expenseWithoutReceiptCount = expenseCounts.withoutReceipt || 0

      return (
        this.matchesPhotoStatus(
          notebookCount,
          this.checkoutPhotoFilter.closingNotebook
        ) &&
        this.matchesPhotoStatus(
          machineCount,
          this.checkoutPhotoFilter.machineReceipt
        ) &&
        this.matchesDepositReceiptStatus(
          depositWithReceiptCount,
          depositWithoutReceiptCount,
          this.checkoutPhotoFilter.depositReceipt
        ) &&
        this.matchesDepositReceiptStatus(
          expenseWithReceiptCount,
          expenseWithoutReceiptCount,
          this.checkoutPhotoFilter.expenseReceipt
        )
      )
    },
    matchesPhotoStatus(count, status) {
      if (!status) return true
      if (status === 'with') return count > 0
      if (status === 'without') return count === 0

      return true
    },
    matchesDepositReceiptStatus(withReceiptCount, withoutReceiptCount, status) {
      if (!status) return true
      if (status === 'with') return withReceiptCount > 0
      if (status === 'without') return withoutReceiptCount > 0

      return true
    },
    clearPhotoFilters() {
      this.checkoutPhotoFilter = {
        closingNotebook: null,
        machineReceipt: null,
        depositReceipt: null,
        expenseReceipt: null
      }
    },
    openPhoto(photo) {
      this.photoPreview = {
        visible: true,
        url: photo.photoUrl
      }
    }
  }
}
</script>

<style scoped>
.checkout-photo-preview {
  display: block;
  max-height: 70vh;
  object-fit: contain;
  width: 100%;
}

.checkout-photo-filter-actions {
  align-items: flex-end;
  display: flex;
  justify-content: flex-end;
}
</style>
